import { BotIcon, HelpCircle } from 'lucide-react'

import { Badge } from 'slab'
import { type AdminComplianceCheckEntityType, type AdminCompliancePolicy } from 'utils/web_api.schemas'

import EmployerComplianceCheckTypeBadge from './EmployerComplianceCheckTypeBadge'
import EmployerComplianceEntityBadge from './EmployerComplianceEntityBadge'

const ComplianceCheckConfirmationRow = ({
  label,
  helpText,
  children
}: {
  label: string
  helpText: string
  children: React.ReactNode
}) => {
  return (
    <div className='flex items-center justify-between'>
      <div className='flex items-center gap-2'>
        <span className='text-sm font-bold text-black'>{label}</span>
        <div className='relative group'>
          <HelpCircle className='h-4 w-4 text-gray-400 cursor-help' />
          <div className='absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none'>
            {helpText}
            <div className='absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900' />
          </div>
        </div>
      </div>
      {children}
    </div>
  )
}

interface ComplianceCheckConfirmationProps {
  entityId: string
  entityName: string
  entityType: AdminComplianceCheckEntityType
  selectedPolicy: AdminCompliancePolicy
}

const ComplianceCheckConfirmation = ({
  entityId,
  entityName,
  entityType,
  selectedPolicy,
}: ComplianceCheckConfirmationProps) => {
  return (
    <div className='border-t border-gray-300 pt-4'>
      <div className='p-4 bg-gray-50 rounded-lg border border-gray-200'>
        <h2 className='text-lg font-semibold text-gray-900 mb-3'>Confirm Details</h2>
        <div className='space-y-3'>
          <ComplianceCheckConfirmationRow
            label='Entity'
            helpText='This is the Employer or Owner we are running this check on'
          >
            <EmployerComplianceEntityBadge
              entityType={entityType}
              entityId={entityId}
              entityName={entityName}
            />
          </ComplianceCheckConfirmationRow>

          <ComplianceCheckConfirmationRow
            label='Provider'
            helpText='This is the compliance partner we are using to run this check'
          >
            <Badge type='success' leftIcon={BotIcon}>Footprint</Badge>
          </ComplianceCheckConfirmationRow>

          <ComplianceCheckConfirmationRow
            label='Type'
            helpText='This defines the verification procedures utilized for this check'
          >
            <EmployerComplianceCheckTypeBadge policyType={selectedPolicy?.policy_type} policyId={selectedPolicy?.id} />
          </ComplianceCheckConfirmationRow>
        </div>
      </div>
    </div>
  )
}

export default ComplianceCheckConfirmation
